import { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'

const FeaturesContext = createContext({ business_central: false, netsuite: false, connectors: true, boomi: false, loaded: false })

// A failed flags fetch is not the same as "the integrations are switched off",
// but the consumer can only see the booleans — so a single transient failure
// used to latch netsuite:false for the whole session, which silently rendered
// NetSuite requests in the generic extraction screen with no way to post and
// no error to explain it. The backend serves these flags from env vars, so a
// failure here is always transport, never a real answer: retry a few times
// with backoff before giving up, and only then fall back.
//
// The fallback direction differs per flag. business_central and netsuite fall
// back to OFF, because rendering an integration that cannot post is worse than
// not showing it. connectors falls back to ON, because every deployment except
// a new marketplace one has connectors, so failing closed would silently strip
// Connectors and Output Destinations from an admin who is entitled to them.
// boomi falls back to OFF for the same reason as business_central/netsuite:
// it gates a real posting integration, so failing open would let requests be
// posted through Boomi on a deployment that was never actually enabled for it.
const RETRY_DELAYS_MS = [500, 1500, 4000]

export function FeaturesProvider({ children }) {
  const [features, setFeatures] = useState({ business_central: false, netsuite: false, connectors: true, boomi: false, loaded: false })

  useEffect(() => {
    let cancelled = false
    let timer

    const attempt = (tryIndex) => {
      axios.get('/api/auth/features')
        .then(res => { if (!cancelled) setFeatures({ ...res.data, loaded: true }) })
        .catch(() => {
          if (cancelled) return
          if (tryIndex < RETRY_DELAYS_MS.length) {
            timer = setTimeout(() => attempt(tryIndex + 1), RETRY_DELAYS_MS[tryIndex])
            return
          }
          setFeatures({ business_central: false, netsuite: false, connectors: true, boomi: false, loaded: true })
        })
    }
    attempt(0)

    return () => { cancelled = true; clearTimeout(timer) }
  }, [])

  return (
    <FeaturesContext.Provider value={features}>
      {children}
    </FeaturesContext.Provider>
  )
}

export function useFeatures() {
  return useContext(FeaturesContext)
}
